// Reading the page tree on the client.  tickets[3302]
//
// The database is the authority on the shape of the tree — subsheet_ancestry is
// maintained by trigger and the cycle check lives there. This is only for
// presenting it: indenting a picker, and keeping the UI from offering a move
// the database will reject.

export interface TreeNode {
  id: string;
  name: string;
  parent_id: string | null;
}

/**
 * Every page beneath `rootId`, not including it.
 *
 * Walks `parent_id` rather than querying the closure so the picker stays
 * responsive while typing. Guards against a cycle even though the database
 * refuses to store one — a client that hangs on malformed data is a worse
 * failure than one that shows a slightly wrong list.
 */
export function descendantIds<T extends TreeNode>(pages: readonly T[], rootId: string): Set<string> {
  const childrenOf = new Map<string, string[]>();
  for (const page of pages) {
    if (!page.parent_id) continue;
    const siblings = childrenOf.get(page.parent_id) ?? [];
    siblings.push(page.id);
    childrenOf.set(page.parent_id, siblings);
  }

  const found = new Set<string>();
  const queue = [...(childrenOf.get(rootId) ?? [])];
  while (queue.length > 0) {
    const id = queue.shift() as string;
    if (found.has(id)) continue;
    found.add(id);
    queue.push(...(childrenOf.get(id) ?? []));
  }
  return found;
}

/**
 * The pages a given page may be moved under: anything except itself and its own
 * descendants. Pass `null` for a page that does not exist yet.
 */
export function validParents<T extends TreeNode>(pages: readonly T[], pageId: string | null): T[] {
  if (!pageId) return [...pages];
  const forbidden = descendantIds(pages, pageId);
  return pages.filter((p) => p.id !== pageId && !forbidden.has(p.id));
}

export interface FlattenedPage<T> {
  page: T;
  depth: number;
}

/**
 * Depth-first order with a depth for each page, so a flat `<Select>` can be
 * indented into something tree-shaped.
 *
 * A page whose parent is missing from `pages` is treated as top-level rather
 * than dropped — a member can be shown a child page while the parent above it
 * is outside what they can read.
 */
export function flattenTree<T extends TreeNode>(pages: readonly T[]): Array<FlattenedPage<T>> {
  const present = new Set(pages.map((p) => p.id));
  const childrenOf = new Map<string | null, T[]>();

  for (const page of pages) {
    const key = page.parent_id && present.has(page.parent_id) ? page.parent_id : null;
    const siblings = childrenOf.get(key) ?? [];
    siblings.push(page);
    childrenOf.set(key, siblings);
  }

  const out: Array<FlattenedPage<T>> = [];
  const seen = new Set<string>();

  const walk = (parent: string | null, depth: number): void => {
    for (const page of childrenOf.get(parent) ?? []) {
      if (seen.has(page.id)) continue;
      seen.add(page.id);
      out.push({ page, depth });
      walk(page.id, depth + 1);
    }
  };
  walk(null, 0);

  // Anything unreachable from a root — only possible with a cycle the database
  // would not have stored. Appended rather than silently lost.
  for (const page of pages) {
    if (!seen.has(page.id)) out.push({ page, depth: 0 });
  }
  return out;
}

/** A page and every page beneath it — the set a department view covers. */
export function subtreeIds<T extends TreeNode>(pages: readonly T[], rootId: string): Set<string> {
  const ids = descendantIds(pages, rootId);
  ids.add(rootId);
  return ids;
}

/**
 * Every page above `pageId`, nearest first in insertion order. Used to open the
 * path to the selected department so it is never hidden in a folded branch.
 * Cycle-safe for the same reason as descendantIds.
 */
export function ancestorIds<T extends TreeNode>(pages: readonly T[], pageId: string): Set<string> {
  const parentOf = new Map(pages.map((p) => [p.id, p.parent_id]));
  const found = new Set<string>();
  let parent = parentOf.get(pageId) ?? null;
  while (parent && !found.has(parent)) {
    found.add(parent);
    parent = parentOf.get(parent) ?? null;
  }
  return found;
}

export interface TreeRow<T> extends FlattenedPage<T> {
  hasChildren: boolean;
  expanded: boolean;
}

/**
 * The rows a collapsible tree draws: flattenTree's order, minus anything under a
 * page that is not in `expanded`. A page is shown only when every page above it
 * is expanded, so folding a parent hides its whole branch however deep.
 */
export function visibleTreeRows<T extends TreeNode>(pages: readonly T[], expanded: ReadonlySet<string>): TreeRow<T>[] {
  const flat = flattenTree(pages);
  const hasChildren = new Set<string>();
  for (let i = 0; i < flat.length - 1; i++) {
    if (flat[i + 1].depth > flat[i].depth) hasChildren.add(flat[i].page.id);
  }

  const out: TreeRow<T>[] = [];
  // Depth at which the current folded branch started; rows deeper than it are hidden.
  let hiddenBelow = Infinity;
  for (const row of flat) {
    if (row.depth > hiddenBelow) continue;
    hiddenBelow = Infinity;
    const isOpen = expanded.has(row.page.id);
    const branch = hasChildren.has(row.page.id);
    out.push({ ...row, hasChildren: branch, expanded: branch && isOpen });
    if (branch && !isOpen) hiddenBelow = row.depth;
  }
  return out;
}

/** Whether a ticket filed in `subsheetIds` belongs to a view covering `scope`. */
export function inScope(subsheetIds: readonly string[] | null | undefined, scope: ReadonlySet<string>): boolean {
  return !!subsheetIds && subsheetIds.some((id) => scope.has(id));
}
